import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, MapPin, Clock, Phone, Quote } from "lucide-react";
import { api } from "@/lib/api";
import { STORE_ADDRESS, STORE_PHONE, STORE_PHONE_TEL, STORE_HOURS, GOOGLE_REVIEW_URL } from "@/constants/store";

const GoldStars = ({ value = 5 }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`w-4 h-4 ${i <= value ? "fill-[#F0B90B] text-[#F0B90B]" : "text-slate-500"}`} />
    ))}
  </div>
);

export default function TestimonialSlider() {
  const [reviews, setReviews] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/reviews/summary").then((r) => setReviews((r.data.recent || []).filter((x) => x.body || x.text)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (reviews.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % reviews.length), 5000);
    return () => clearInterval(t);
  }, [reviews.length]);

  const r = reviews[idx];

  return (
    <section className="bg-[#071330] text-white relative overflow-hidden" data-testid="testimonial-slider">
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#F0B90B]/10 rounded-full blur-3xl pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-8 py-16 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="ipv-eyebrow text-[#F0B90B]">Real shoppers · Grand Rapids, MI</div>
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-3">
            Trusted in-store.<br />
            <span className="text-[#F0B90B]">Now trusted online.</span>
          </h2>

          {r ? (
            <div className="mt-8 min-h-[150px]">
              <AnimatePresence mode="wait">
                <motion.figure
                  key={idx}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.45 }}
                  data-testid="testimonial-quote"
                >
                  <Quote className="w-7 h-7 text-[#F0B90B]/60" />
                  <blockquote className="mt-3 text-lg leading-relaxed text-slate-100/90 max-w-lg">
                    “{r.body || r.text}”
                  </blockquote>
                  <figcaption className="mt-4 flex items-center gap-3">
                    <GoldStars value={r.rating || 5} />
                    <span className="font-bold text-sm">{r.author_name || r.name || "Verified shopper"}</span>
                    <span className="text-xs text-slate-400">Verified purchase</span>
                  </figcaption>
                </motion.figure>
              </AnimatePresence>
              {reviews.length > 1 && (
                <div className="flex gap-2 mt-6">
                  {reviews.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setIdx(i)}
                      className={`h-1.5 rounded-full transition-[width,background-color] ${i === idx ? "w-7 bg-[#F0B90B]" : "w-3 bg-white/25"}`}
                      data-testid={`testimonial-dot-${i}`}
                      aria-label={`Review ${i + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="mt-8" data-testid="testimonial-empty">
              <GoldStars value={5} />
              <p className="mt-3 text-slate-100/80 leading-relaxed max-w-lg">
                Hundreds of West Michigan shoppers walk our floor every week. Bought
                something you love? Your review lands right here.
              </p>
              <a
                href={GOOGLE_REVIEW_URL}
                target="_blank"
                rel="noreferrer"
                className="mt-5 inline-flex items-center gap-2 rounded-full border-2 border-[#F0B90B] text-[#F0B90B] font-bold px-6 py-2.5 hover:bg-[#F0B90B] hover:text-[#0A1E5E] transition-colors text-sm"
                data-testid="testimonial-google-cta"
              >
                ★ Review us on Google
              </a>
            </div>
          )}
        </div>

        {/* Always-visible store trust card */}
        <div className="bg-[#0A1E5E]/70 backdrop-blur-md border border-[#F0B90B]/30 rounded-lg p-7 lg:p-9" data-testid="store-trust-card">
          <div className="ipv-eyebrow text-[#F0B90B]">Visit the showroom</div>
          <div className="mt-5 space-y-5 text-sm">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-[#F0B90B] shrink-0 mt-0.5" />
              <div>
                <div className="font-bold">{STORE_ADDRESS.street}</div>
                <div className="text-slate-300">{STORE_ADDRESS.city}, {STORE_ADDRESS.state} {STORE_ADDRESS.postal}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-[#F0B90B] shrink-0 mt-0.5" />
              <div className="space-y-0.5">
                {STORE_HOURS.displayLines.map((l) => (
                  <div key={l.days}>
                    <span className="font-bold">{l.days}</span>
                    <span className="text-slate-300"> · {l.hours}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <a
            href={`tel:${STORE_PHONE_TEL}`}
            className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#F0B90B] hover:bg-[#FFC220] text-[#0A1E5E] font-black py-3.5 shadow-[0_0_18px_rgba(240,185,11,0.35)] transition-colors [touch-action:manipulation]"
            data-testid="trust-card-call-btn"
          >
            <Phone className="w-4 h-4" /> Tap to call · {STORE_PHONE}
          </a>
        </div>
      </div>
    </section>
  );
}
